import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from '../utils/antdMessage';
import { bindMapTrackLineClicks } from '../utils/dashboard/trackLineClickHelpers';
import { formatTrackExport, downloadTextFile } from '../utils/dashboard/trackHelpers';

/**
 * 指挥页：点击轨迹线 → 操作菜单（下载 / 导出 / 停止 / 隐藏 / 清除）
 */
export default function useDashboardTrackLineActions({
  getMap,
  enabled,
  realTimeTrackData,
  realTimeTrackDrawing,
  trackData,
  members,
  openDownloadTrackModal,
  stopTrackFeatures,
  clearRealtimeTrack,
  hideRealtimeTrack,
  setShowTracks,
  onSendLocation,
  onSendLocationDisplay
}) {
  const [actionModal, setActionModal] = useState({
    visible: false,
    memberName: '',
    kind: 'realtime'
  });
  const unbindRef = useRef(null);

  const closeActionModal = useCallback(() => {
    setActionModal((prev) => ({ ...prev, visible: false }));
  }, []);

  const handleTrackClick = useCallback((info) => {
    if (!info?.memberName) return;
    setActionModal({
      visible: true,
      memberName: info.memberName,
      kind: info.kind || 'realtime',
      displayLng: Number(info.lngLat?.lng),
      displayLat: Number(info.lngLat?.lat)
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let tries = 0;

    const tryBind = () => {
      if (cancelled) return;
      const map = getMap?.();
      if (!map || !map.isStyleLoaded?.()) {
        tries += 1;
        if (tries < 80) setTimeout(tryBind, 250);
        return;
      }
      unbindRef.current?.();
      unbindRef.current = bindMapTrackLineClicks(map, handleTrackClick);
    };

    tryBind();
    return () => {
      cancelled = true;
      unbindRef.current?.();
      unbindRef.current = null;
    };
  }, [enabled, getMap, handleTrackClick]);

  const resolveMember = useCallback((name) => {
    return (members || []).find((m) => m.name === name) || { name };
  }, [members]);

  const pointCount = (() => {
    const name = actionModal.memberName;
    if (!name) return 0;
    if (actionModal.kind === 'realtime') {
      return (realTimeTrackData?.[name] || []).length;
    }
    return (trackData?.[name] || []).length;
  })();

  const isDrawing = actionModal.memberName
    ? realTimeTrackDrawing?.has?.(actionModal.memberName)
    : false;

  const onDownload = useCallback(() => {
    const member = resolveMember(actionModal.memberName);
    closeActionModal();
    openDownloadTrackModal?.(member);
  }, [actionModal.memberName, resolveMember, closeActionModal, openDownloadTrackModal]);

  const onExportDrawn = useCallback((format) => {
    const name = actionModal.memberName;
    const points = realTimeTrackData?.[name] || [];
    const exported = formatTrackExport(name, points, format);
    if (!exported) {
      message.warning('当前没有可导出的绘制轨迹点');
      return;
    }
    downloadTextFile(exported.content, exported.filename);
    message.success(`已导出 ${name} 绘制轨迹（${points.length} 点）`);
    closeActionModal();
  }, [actionModal.memberName, realTimeTrackData, closeActionModal]);

  const onStopDrawing = useCallback(() => {
    const name = actionModal.memberName;
    stopTrackFeatures?.(name);
    message.success(`已停止绘制 ${name}（轨迹已保留）`);
    closeActionModal();
  }, [actionModal.memberName, stopTrackFeatures, closeActionModal]);

  const onClearDrawn = useCallback(() => {
    const name = actionModal.memberName;
    clearRealtimeTrack?.(name);
    message.success(`已清除 ${name} 的绘制轨迹`);
    closeActionModal();
  }, [actionModal.memberName, clearRealtimeTrack, closeActionModal]);

  const onHideHistory = useCallback(() => {
    const name = actionModal.memberName;
    setShowTracks?.((prev) => ({ ...prev, [name]: false }));
    message.info(`已隐藏 ${name} 的历史轨迹`);
    closeActionModal();
  }, [actionModal.memberName, setShowTracks, closeActionModal]);

  const onHideRealtime = useCallback(() => {
    const name = actionModal.memberName;
    hideRealtimeTrack?.(name);
    message.info(`已隐藏 ${name} 的绘制轨迹（数据保留，可再显示）`);
    closeActionModal();
  }, [actionModal.memberName, hideRealtimeTrack, closeActionModal]);

  const lastPoint = (() => {
    const name = actionModal.memberName;
    if (!name) return null;
    const arr = actionModal.kind === 'realtime'
      ? (realTimeTrackData?.[name] || [])
      : (trackData?.[name] || []);
    if (!arr.length) return null;
    const p = arr[arr.length - 1];
    const lat = Number(p?.lat ?? p?.latitude);
    const lng = Number(p?.lng ?? p?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
  })();

  const onSendLocationClick = useCallback(() => {
    const name = actionModal.memberName;
    const member = resolveMember(name);
    const displayLng = Number(actionModal.displayLng);
    const displayLat = Number(actionModal.displayLat);
    if (Number.isFinite(displayLng) && Number.isFinite(displayLat)) {
      closeActionModal();
      onSendLocationDisplay?.(displayLng, displayLat, { member });
      return;
    }
    const arr = actionModal.kind === 'realtime'
      ? (realTimeTrackData?.[name] || [])
      : (trackData?.[name] || []);
    const p = arr.length ? arr[arr.length - 1] : null;
    const lat = Number(p?.lat ?? p?.latitude);
    const lng = Number(p?.lng ?? p?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      message.warning('当前轨迹没有可用坐标');
      return;
    }
    closeActionModal();
    onSendLocation?.(lat, lng, { member });
  }, [
    actionModal.memberName,
    actionModal.kind,
    actionModal.displayLng,
    actionModal.displayLat,
    realTimeTrackData,
    trackData,
    closeActionModal,
    onSendLocation,
    onSendLocationDisplay,
    resolveMember
  ]);

  return {
    trackLineActionModal: actionModal,
    trackLineActionProps: {
      visible: actionModal.visible,
      memberName: actionModal.memberName,
      kind: actionModal.kind,
      pointCount,
      isDrawing,
      canSendLocation: !!lastPoint
        || (Number.isFinite(actionModal.displayLng) && Number.isFinite(actionModal.displayLat)),
      onClose: closeActionModal,
      onDownload,
      onExportDrawn,
      onStopDrawing,
      onClearDrawn,
      onHideHistory,
      onHideRealtime,
      onSendLocation: onSendLocationClick
    }
  };
}
